import React from 'react';
import CustomAlert from '@common/components/Feedback/CustomAlert';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useAppSelector } from 'store';
import { useTranslation } from 'react-i18next';
import { selectRobotsData } from 'store/robots/robots-slice-selectors';
import DataSyncButton from '../DataSyncButton';
import RobotConnectionIcon from 'components/robots/status-info/RobotConnectionIcon';
import SphereLoginBackdrop from '../../../../media/sphereLoginBackdrop.png';
import WebSocketConnectionStatus from 'models/enums/websocket-connection-status';
import { InfoIcon } from '@common/utils/icons/icons';
import { replaceArgsInMessage } from '@common/utils/strings';
import { isVersionAGreaterThanB } from '@common/utils/version-util';

const INTERACTION_MIN_VERSION = '2026.1.0';

const InteractionAnnouncementPage: React.FC = () => {
  const { t } = useTranslation();

  const robotData = useAppSelector((state) => selectRobotsData(state));

  const robotVersion = robotData.currentRobot?.softwareVersion?.rswSoftwareVersion;
  const needsUpdating =
    robotVersion != null ? isVersionAGreaterThanB(INTERACTION_MIN_VERSION, robotVersion) : null;

  return (
    <React.Fragment>
      <Container maxWidth="md">
        <Stack spacing={2} width="100%">
          <Box
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              textAlign: 'center',
              py: { xs: 3, sm: 4 },
              px: { xs: 2, sm: '10%' },
              backgroundImage: `url(${SphereLoginBackdrop})`,
              backgroundPosition: 'center',
              backgroundSize: 'cover',
            }}
          >
            <Typography
              variant="h1"
              sx={{
                textShadow: '0 0 8px #fff',
                fontSize: { xs: '1.5rem', sm: '4rem' },
              }}
            >
              {t('interaction-announcement-header')}
            </Typography>
          </Box>
          <CustomAlert severity="info" header={t('interaction-announcement-info-alert')} />

          <Typography variant="body2">{t('interaction-announcement-setup')}</Typography>

          <Stack spacing={2} pt={2} sx={{ opacity: needsUpdating !== true ? 0.5 : undefined }}>
            <Typography variant="h2">
              {replaceArgsInMessage(t('interaction-announcement-step-one-title'), [
                needsUpdating ? '' : t('interaction-announcement-up-to-date'),
              ])}
            </Typography>
            <Typography variant="body2">
              {replaceArgsInMessage(t('interaction-announcement-step-one-text'), [
                INTERACTION_MIN_VERSION,
                robotVersion ?? '-',
                needsUpdating ? t('interaction-announcement-please-update') : '',
              ])}
            </Typography>
          </Stack>

          <Stack spacing={2} pt={2} sx={{ opacity: needsUpdating ? 0.5 : undefined }}>
            <Typography variant="h2">{t('interaction-announcement-step-two-title')}</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <RobotConnectionIcon />
              <Typography variant="body2">{t('interaction-announcement-robot-online')}</Typography>
            </Stack>
            <Typography variant="body2">
              {replaceArgsInMessage(t('interaction-announcement-step-two-text'), [
                robotData.currentRobot?.name ?? '-',
              ])}
            </Typography>
            <DataSyncButton
              activeOverride
              forceDisable={
                robotData.currentRobot?.status?.active === false ||
                robotData.robotHubState === WebSocketConnectionStatus.DISCONNECTED
              }
            />
            <Stack alignItems="center" direction="row" spacing={1}>
              <InfoIcon fontSize="small" color="action" />
              <Typography variant="caption">
                {t('interaction-announcement-trigger-sync-help-text')}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Container>
    </React.Fragment>
  );
};

export default InteractionAnnouncementPage;
